import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomFileUploader,
  CustomLoading,
  CustomModal,
  CustomButton,
  CustomSectionTitle,
  FixedActionBar,
} from "@ippon/shared-components";
import { extractS3KeyFromUrl } from "@ippon/shared-utils";
import { fetchPropertySectionById } from "@/store/propertySectionSlice";
import { fetchUnitById, updateUnit } from "@/store/unitSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { AppDispatch, RootState } from "@/store";
import UnitTabForm, {
  type UnitFormSubmitValues,
} from "@/components/PropertyDetail/Unit/UnitTabForm";
import { uploadPendingUnitDocuments } from "@/components/PropertyDetail/Unit/sections/uploadUnitDocuments";
import { processFileArray } from "@/utils/fileUpload";
import type { UnitDocument } from "@ippon/shared-types";

interface SectionTabFormProps {
  sectionId: string;
  /** Unit→PropertySection adapter で渡される旧 shape (主に section_images の参照に使用) */
  current: any;
  /** 附帯収益区画かどうか (現状はラベル/見た目調整に未使用) */
  isAncillary: boolean;
  dispatch: any;
  occupancyStatus?: "occupied" | "vacant";
  embedded?: boolean;
  /** 親 (PreviewDrawer 等) に未保存(dirty)状態を伝え、閉じ確認を出させる。 */
  onDirtyChange?: (isDirty: boolean) => void;
}

const FORM_ID = "section-tab-unit-form";

const SectionTabForm: React.FC<SectionTabFormProps> = ({
  sectionId,
  current,
  embedded = false,
  onDirtyChange,
}) => {
  const { showToast } = useGlobalToast();
  const appDispatch = useDispatch<AppDispatch>();
  const unit = useSelector((s: RootState) => s.units.current.data);
  const unitLoading = useSelector((s: RootState) => s.units.current.loading);
  const activeContract = useSelector((s: RootState) =>
    (s.contracts.byUnit[sectionId]?.data || []).find(
      (contract) => contract.status === "active",
    ),
  );

  const [saving, setSaving] = useState(false);
  // UnitTabForm 内のフォームの dirty 状態を上位に拾い、FixedActionBar の disabled に反映
  const [isDirty, setIsDirty] = useState(false);
  // 区画画像の編集状態 (FixedActionBar の disabled / submit 時の保存判定に利用)
  const [imageDirty, setImageDirty] = useState(false);
  const [pendingAreaValues, setPendingAreaValues] =
    useState<UnitFormSubmitValues | null>(null);

  // 親 (PreviewDrawer) へ未保存状態を通知 (区画情報 or 画像のどちらかが dirty)
  useEffect(() => {
    onDirtyChange?.(isDirty || imageDirty);
  }, [isDirty, imageDirty, onDirtyChange]);

  // ── Unit を直接取得 ──
  useEffect(() => {
    if (!sectionId) return;
    void appDispatch(fetchUnitById({ unit_id: sectionId }));
  }, [appDispatch, sectionId]);

  const initialValues = useMemo<
    Partial<UnitFormSubmitValues> | undefined
  >(() => {
    if (!unit || unit.unit_id !== sectionId) return undefined;
    return {
      area_type: unit.area_type,
      landmark_name: unit.landmark_name,
      occupancy_status: unit.occupancy_status,
      revenue_type: unit.revenue_type,
      revenue_item_category_id: unit.revenue_item_category_id,
      floor_plan: unit.floor_plan,
      notes: unit.notes,
      private_detail: unit.private_detail,
      common_detail: unit.common_detail,
      facility_detail: unit.facility_detail,
      land_detail: unit.land_detail,
      budget: unit.budget,
      revenue: unit.revenue,
      contract: unit.contract,
      custom_fields: unit.custom_fields,
    };
  }, [unit, sectionId]);

  // ── 区画画像 (section_images) ── 旧 SectionTabForm の挙動を踏襲
  const imageForm = useForm<{ viewFiles: any[] }>({
    defaultValues: { viewFiles: [] },
  });

  const handleSubmit = useCallback(
    async (values: UnitFormSubmitValues, areaChangeConfirmed = false) => {
      const previousArea = Number(
        unit?.area_type === "common"
          ? unit.common_detail?.area_tsubo
          : unit?.private_detail?.area_tsubo,
      );
      const nextArea = Number(
        values.area_type === "common"
          ? values.common_detail?.area_tsubo
          : values.private_detail?.area_tsubo,
      );
      const areaChanged =
        previousArea > 0 && nextArea > 0 && previousArea !== nextArea;
      const hasActiveRent =
        unit?.revenue_type === "stock_rent" &&
        !!activeContract &&
        (Number(activeContract.monthly_rent || 0) > 0 ||
          Number(activeContract.maintenance_fee || 0) > 0);
      if (areaChanged && hasActiveRent && !areaChangeConfirmed) {
        setPendingAreaValues(values);
        return;
      }
      setSaving(true);
      try {
        const propId = current?.property_id || unit?.property_id || "";

        // 添付書類 (UnitOtherDocuments) の正規化。
        // area_type=common なら common_detail.documents、facility なら facility_detail.documents から取り出す。
        const detailDocs: UnitDocument[] | undefined =
          values.area_type === "common"
            ? values.common_detail?.documents
            : values.area_type === "facility"
              ? values.facility_detail?.documents
              : undefined;
        let normalizedDocs: UnitDocument[] = [];
        try {
          normalizedDocs = await uploadPendingUnitDocuments(
            detailDocs || [],
            propId,
          );
        } catch {
          showToast({
            type: "error",
            message: "添付書類のアップロードに失敗しました",
          });
          return;
        }

        // 区画画像 (imageForm.viewFiles) を S3 にアップロードして UnitImage[] を構築
        const viewFiles = imageForm.getValues("viewFiles") || [];
        const normalizedViewFiles = viewFiles
          .map((item: any) => {
            if (!item) return null;
            if (item instanceof File) return item;
            if (typeof item === "string") return item;
            if (
              item.data &&
              typeof item.data === "string" &&
              item.originalFileName &&
              item.mimeType
            ) {
              return item;
            }
            if (item.s3Key && typeof item.s3Key === "string") {
              return item.s3Key;
            }
            if (
              item.public_file_path &&
              typeof item.public_file_path === "string"
            ) {
              return item.public_file_path;
            }
            return null;
          })
          .filter(Boolean);

        let images: Array<{
          image_id: string;
          file_name: string;
          s3_key: string;
          uploaded_at: string;
        }> = [];
        try {
          const imageKeys = await processFileArray(normalizedViewFiles, propId);
          images = imageKeys.map((key, idx) => {
            const raw = viewFiles[idx] || {};
            return {
              image_id:
                raw.image_id ||
                `img-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
              file_name:
                raw.originalFileName ||
                raw.name ||
                raw.file_name ||
                `section_image_${idx + 1}`,
              s3_key: key,
              uploaded_at: raw.uploaded_at || new Date().toISOString(),
            };
          });
        } catch {
          showToast({
            type: "error",
            message: "画像のアップロードに失敗しました",
          });
          return;
        }

        // area_type ごとに *_detail にアップロード後の images / documents を内包させる
        const privateDetail =
          values.area_type === "private"
            ? { ...(values.private_detail ?? {}), images }
            : values.private_detail;
        const commonDetail =
          values.area_type === "common"
            ? { ...(values.common_detail ?? {}), documents: normalizedDocs }
            : values.common_detail;
        const facilityDetail =
          values.area_type === "facility"
            ? { ...(values.facility_detail ?? {}), documents: normalizedDocs }
            : values.facility_detail;

        const result = await appDispatch(
          updateUnit({
            unit_id: sectionId,
            body: {
              area_type: values.area_type,
              landmark_name: values.landmark_name,
              occupancy_status: values.occupancy_status,
              is_parking: values.is_parking,
              revenue_type: values.revenue_type,
              // 付帯のとき収益項目を送信。付帯以外は null で REMOVE。
              revenue_item_category_id: values.revenue_item_category_id ?? null,
              floor_plan: values.floor_plan ?? null,
              notes: values.notes ?? null,
              private_detail: privateDetail ?? null,
              common_detail: commonDetail ?? null,
              facility_detail: facilityDetail ?? null,
              land_detail: values.land_detail ?? null,
              budget: values.budget ?? null,
              contract: values.contract ?? null,
              custom_fields: values.custom_fields,
            },
          }),
        );
        if (updateUnit.fulfilled.match(result)) {
          await Promise.all([
            appDispatch(fetchUnitById({ unit_id: sectionId })),
            appDispatch(
              fetchPropertySectionById({ property_section_id: sectionId }),
            ),
          ]);
          setImageDirty(false);
          showToast({ type: "success", message: "更新しました" });
        } else {
          showToast({ type: "error", message: "更新に失敗しました" });
        }
      } catch {
        showToast({ type: "error", message: "更新に失敗しました" });
      } finally {
        setSaving(false);
      }
    },
    [
      appDispatch,
      sectionId,
      showToast,
      current?.property_id,
      unit?.property_id,
      imageForm,
      activeContract,
      unit,
    ],
  );

  const decodeFileNameFromUrl = useCallback((url: string) => {
    if (!url) return "";
    try {
      const parsed = new URL(String(url));
      const raw = parsed.pathname.split("/").pop() || "";
      return decodeURIComponent(raw);
    } catch {
      const withoutQuery = String(url).split("?")[0];
      const raw = withoutQuery.split("/").pop() || withoutQuery;
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }, []);

  // 区画画像は private_detail.images から取得 (添付書類 documents とは完全に独立した別フィールド)
  const sectionImages = useMemo(() => {
    const imgs = unit?.private_detail?.images ?? [];
    return imgs
      .filter((img) => img?.s3_key)
      .map((img, index: number) => {
        const url = img.s3_key;
        const key =
          typeof url === "string"
            ? extractS3KeyFromUrl(url) || url
            : String(index);
        return {
          id: `${key}-${index}`,
          public_file_path: url,
          name: img.file_name || decodeFileNameFromUrl(url),
          s3Key: key,
          isUrl: true,
        };
      });
  }, [unit?.private_detail?.images, decodeFileNameFromUrl]);

  const [uploaderInitialImages, setUploaderInitialImages] = useState<any[]>([]);
  const hasInitializedUploaderRef = useRef(false);
  // sectionId が変わったら初期化フラグをリセット (区画切り替え時に新画像をロードする)
  useEffect(() => {
    hasInitializedUploaderRef.current = false;
  }, [sectionId]);
  useEffect(() => {
    if (hasInitializedUploaderRef.current) return;
    if (sectionImages.length === 0 && !current) return;
    imageForm.setValue("viewFiles", sectionImages, { shouldDirty: false });
    setUploaderInitialImages(sectionImages);
    hasInitializedUploaderRef.current = true;
  }, [sectionImages, imageForm, current]);

  // Unit が未取得ならローディングを出す (画像も Unit メタも揃ってから描画)
  if (!initialValues || unitLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, pb: 3 }}>
      <CustomModal
        openModal={!!pendingAreaValues}
        handleCloseModal={() => setPendingAreaValues(null)}
        title="坪数変更の確認"
        bodyText={
          "賃料または共益費が登録されています。\n契約金額・坪単価は変更せず、坪数のみ更新します。"
        }
        bodyTextAlign="left"
        hideCloseButton
        modalType="deleteModal"
        sx={{ padding: "0 !important" }}
      >
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const values = pendingAreaValues;
            setPendingAreaValues(null);
            if (values) void handleSubmit(values, true);
          }}
          sx={{ display: "flex", justifyContent: "center", gap: 2 }}
        >
          <CustomButton
            label="キャンセル"
            buttonCategory="cancelModal"
            type="button"
            onClick={() => setPendingAreaValues(null)}
            sx={{ width: "50% !important" }}
          />
          <CustomButton
            label="更新"
            buttonCategory="submit"
            type="submit"
            disabled={saving}
            sx={{ width: "50% !important" }}
          />
        </Box>
      </CustomModal>
      {/* 画像 (全 area_type 共通) */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <CustomSectionTitle title="画像" addBorder size="small" />
        </Box>
        <CustomFileUploader
          name="viewFiles"
          register={imageForm.register as unknown as any}
          setValue={imageForm.setValue as unknown as any}
          initialImages={uploaderInitialImages}
          showLabel="true"
          width="180px"
          errors={imageForm.formState.errors}
          onImageChange={() => setImageDirty(true)}
        />
      </Box>

      {/* 区画情報 — UnitTabForm を内包。
          ・区画詳細では契約系 (テナント管理タブの責務) を非表示
          ・収益あり時の予算は UnitTabForm 内ロジックで自動表示 */}
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="共通情報" addBorder size="small" />
      </Box>
      <UnitTabForm
        propertyId={current?.property_id || unit?.property_id || ""}
        initialValues={initialValues}
        onSubmit={(values) => handleSubmit(values)}
        saving={saving}
        formId={FORM_ID}
        omitContract
        hideBasicTitle
        disableAreaTypeSelect
        hideActionBar
        onDirtyChange={setIsDirty}
        submitLabel="更新"
        labelWidth="120px"
        inputWidth="600px"
      />

      {/* 画面下部の固定更新バー (旧 SectionTabForm と同じ FixedActionBar) */}
      <FixedActionBar
        submitLabel="更新"
        isLoading={saving}
        disabled={(!isDirty && !imageDirty) || saving}
        embedded={embedded}
        onSubmit={() => {
          // 区画画像 / Unit 情報 / 添付書類 を一括で UnitTabForm の submit ハンドラ経由で保存
          const el = document.getElementById(FORM_ID) as HTMLFormElement | null;
          el?.requestSubmit();
        }}
      />
    </Box>
  );
};

export default SectionTabForm;
